import api from './client';
import type {
  CategoryAnalysisEntry,
  CreateSessionResponse,
  MissingFieldFlags,
  PhaseOneExtractionResponse,
  PhaseOneMenuCategory,
  PhaseOneMenuData,
  PhaseOneMenuProduct,
  PhaseTwoAnalysisResponse,
  PhaseTwoMetadataRequest,
  RecommendationEntry,
  SessionStatusResponse,
  UploadUrlResponse,
  SubmitFeedbackRequest
} from '../types';

const coalesce = <T>(...values: (T | undefined | null)[]): T | undefined => {
  for (const value of values) {
    if (value !== undefined && value !== null) {
      return value;
    }
  }
  return undefined;
};

const requireValue = <T>(value: T | undefined, errorMessage: string): T => {
  if (value === undefined) {
    throw new Error(errorMessage);
  }
  return value;
};

const toCreateSessionResponse = (payload: any): CreateSessionResponse => ({
  sessionId: requireValue(
    coalesce<string>(payload?.sessionId, payload?.SessionId),
    'Session ID was missing from the Labs API response.'
  )
});

const toUploadUrlResponse = (payload: any): UploadUrlResponse => ({
  baseUrl: requireValue(
    coalesce<string>(payload?.baseUrl, payload?.BaseUrl),
    'Upload base URL was missing from the Labs API response.'
  ),
  sasToken: coalesce<string>(payload?.sasToken, payload?.SasToken, '') ?? '',
  folder: coalesce<string | null>(payload?.folder, payload?.Folder, null) ?? null,
  filename: requireValue(
    coalesce<string>(payload?.filename, payload?.Filename),
    'Upload filename was missing from the Labs API response.'
  ),
  blobName: requireValue(
    coalesce<string>(payload?.blobName, payload?.BlobName),
    'Upload blob name was missing from the Labs API response.'
  ),
  expiresOnUtc: requireValue(
    coalesce<string>(payload?.expiresOnUtc, payload?.ExpiresOnUtc),
    'Upload expiration was missing from the Labs API response.'
  )
});

const toPhaseOneMenuProduct = (payload: any): PhaseOneMenuProduct => ({
  name: requireValue(
    coalesce<string>(payload?.name, payload?.Name),
    'Menu product name was missing from the Labs API response.'
  ),
  price: coalesce<number | null>(payload?.price, payload?.Price, null) ?? null,
  description: coalesce<string | null>(payload?.description, payload?.Description, null) ?? null
});

const toPhaseOneMenuCategory = (payload: any): PhaseOneMenuCategory => ({
  categoryName: requireValue(
    coalesce<string>(payload?.categoryName, payload?.CategoryName),
    'Menu category name was missing from the Labs API response.'
  ),
  products: Array.isArray(payload?.products ?? payload?.Products)
    ? (payload?.products ?? payload?.Products).map(toPhaseOneMenuProduct)
    : []
});

const toPhaseOneMenuData = (payload: any): PhaseOneMenuData => ({
  menuCategory: requireValue(
    coalesce<'Bar' | 'Food' | 'Hybrid'>(payload?.menuCategory, payload?.MenuCategory),
    'Menu category was missing from the Labs API response.'
  ),
  eventName: coalesce<string | null>(payload?.eventName, payload?.EventName, null) ?? null,
  eventType: coalesce<string | null>(payload?.eventType, payload?.EventType, null) ?? null,
  eventLocation: coalesce<string | null>(payload?.eventLocation, payload?.EventLocation, null) ?? null,
  eventDate: coalesce<string | null>(payload?.eventDate, payload?.EventDate, null) ?? null,
  taxIncluded:
    coalesce<'Included' | 'Additional' | 'NotApplicable' | null>(payload?.taxIncluded, payload?.TaxIncluded, null) ?? null,
  serviceFeeIncluded:
    coalesce<'Included' | 'Additional' | 'NotApplicable' | null>(
      payload?.serviceFeeIncluded,
      payload?.ServiceFeeIncluded,
      null
    ) ?? null,
  taxRate: coalesce<number | null>(payload?.taxRate, payload?.TaxRate, null) ?? null,
  serviceFeeRate: coalesce<number | null>(payload?.serviceFeeRate, payload?.ServiceFeeRate, null) ?? null,
  menuData: {
    categories: Array.isArray(payload?.menuData?.categories ?? payload?.MenuData?.Categories)
      ? (payload?.menuData?.categories ?? payload?.MenuData?.Categories).map(toPhaseOneMenuCategory)
      : []
  }
});

const toMissingFlags = (payload: any): MissingFieldFlags => ({
  needsEventType: Boolean(coalesce(payload?.needsEventType, payload?.NeedsEventType, false)),
  needsEventLocation: Boolean(coalesce(payload?.needsEventLocation, payload?.NeedsEventLocation, false)),
  needsEventDate: Boolean(coalesce(payload?.needsEventDate, payload?.NeedsEventDate, false)),
  needsTaxIncluded: Boolean(coalesce(payload?.needsTaxIncluded, payload?.NeedsTaxIncluded, false)),
  needsServiceFeeIncluded: Boolean(
    coalesce(payload?.needsServiceFeeIncluded, payload?.NeedsServiceFeeIncluded, false)
  )
});

const toPhaseOneExtractionResponse = (payload: any): PhaseOneExtractionResponse => ({
  sessionId: requireValue(
    coalesce<string>(payload?.sessionId, payload?.SessionId),
    'Session ID was missing from the extraction response.'
  ),
  menu: toPhaseOneMenuData(payload?.menu ?? payload?.Menu ?? {}),
  missing: toMissingFlags(payload?.missing ?? payload?.Missing ?? {})
});

const toCategoryAnalysisEntry = (payload: any): CategoryAnalysisEntry => ({
  category: requireValue(
    coalesce<string>(payload?.category, payload?.Category),
    'Category name was missing from the analysis response.'
  ),
  ranking: requireValue(
    coalesce<string>(payload?.ranking, payload?.Ranking),
    'Category ranking was missing from the analysis response.'
  ),
  averagePrice: Number(coalesce<number>(payload?.averagePrice, payload?.AveragePrice, 0)),
  priceRange: {
    min: Number(coalesce<number>(payload?.priceRange?.min, payload?.PriceRange?.Min, 0)),
    max: Number(coalesce<number>(payload?.priceRange?.max, payload?.PriceRange?.Max, 0))
  },
  comparisonNotes: requireValue(
    coalesce<string>(payload?.comparisonNotes, payload?.ComparisonNotes),
    'Comparison notes were missing from the analysis response.'
  )
});

const toRecommendationEntry = (payload: any): RecommendationEntry => ({
  type: requireValue(
    coalesce<'Increase' | 'Decrease' | 'Optimize'>(payload?.type, payload?.Type),
    'Recommendation type was missing from the analysis response.'
  ),
  itemCategory: requireValue(
    coalesce<string>(payload?.itemCategory, payload?.ItemCategory),
    'Recommendation category was missing from the analysis response.'
  ),
  currentPriceRange: requireValue(
    coalesce<string>(payload?.currentPriceRange, payload?.CurrentPriceRange),
    'Recommendation price range was missing from the analysis response.'
  ),
  suggestedPriceRange: requireValue(
    coalesce<string>(payload?.suggestedPriceRange, payload?.SuggestedPriceRange),
    'Recommendation suggested range was missing from the analysis response.'
  ),
  rationale: requireValue(
    coalesce<string>(payload?.rationale, payload?.Rationale),
    'Recommendation rationale was missing from the analysis response.'
  ),
  potentialRevenueImpact: requireValue(
    coalesce<string>(payload?.potentialRevenueImpact, payload?.PotentialRevenueImpact),
    'Recommendation revenue impact was missing from the analysis response.'
  )
});

const toPhaseTwoAnalysisResponse = (payload: any): PhaseTwoAnalysisResponse => {
  const report = payload?.report ?? payload?.Report ?? {};
  const metadata = payload?.metadata ?? payload?.Metadata ?? null;
  const overall = report?.overall ?? report?.Overall ?? {};
  const comparative = report?.comparative ?? report?.Comparative ?? null;
  const currentMenu = report?.currentMenuBreakdown ?? report?.CurrentMenuBreakdown ?? null;
  const detailedRecs = report?.detailedRecommendations ?? report?.DetailedRecommendations ?? null;
  const additionalRecCards = report?.additionalRecommendationCards ?? report?.AdditionalRecommendationCards ?? null;
  const impactRaw = report?.impact ?? report?.Impact ?? null;

  return {
    sessionId: requireValue(
      coalesce<string>(payload?.sessionId, payload?.SessionId),
      'Session ID was missing from the analysis response.'
    ),
    overallAnalysis: {
      pricingSpectrumPlacement: requireValue(
        coalesce<
          'Neighborhood Bar'
          | 'Standard Cocktail Lounge'
          | 'Festival / Ticketed Event'
          | 'Stadium / Arena Pricing'
          | 'Ultra-Premium / VIP Event'
        >(overall?.pricingSpectrumPlacement, overall?.PricingSpectrumPlacement),
        'Pricing spectrum placement was missing from the analysis response.'
      ),
      overallScore: Number(coalesce<number>(overall?.overallScore, overall?.OverallScore, 0)),
      summary: requireValue(
        coalesce<string>(overall?.summary, overall?.Summary),
        'Analysis summary was missing from the analysis response.'
      )
    },
    categoryAnalysis: Array.isArray(report?.categoryAnalysis ?? report?.CategoryAnalysis ?? report?.categories)
      ? (report?.categoryAnalysis ?? report?.CategoryAnalysis ?? report?.categories).map(
          toCategoryAnalysisEntry
        )
      : [],
    recommendations: Array.isArray(report?.recommendations ?? report?.Recommendations)
      ? (report?.recommendations ?? report?.Recommendations).map(toRecommendationEntry)
      : [],
    comparativeData: comparative
      ? {
          headers: Array.isArray(comparative?.headers ?? comparative?.Headers)
            ? (comparative?.headers ?? comparative?.Headers)
            : [],
          rows: Array.isArray(comparative?.rows ?? comparative?.Rows)
            ? (comparative?.rows ?? comparative?.Rows)
            : []
        }
      : undefined,
    currentMenuBreakdown: Array.isArray(currentMenu)
      ? currentMenu.map((cat: any) => ({
          category: coalesce<string>(cat?.category, cat?.Category) ?? 'Unknown',
          items: Array.isArray(cat?.items ?? cat?.Items)
            ? (cat?.items ?? cat?.Items).map((it: any) => ({
                name: coalesce<string>(it?.name, it?.Name) ?? 'Unnamed',
                basePrice: coalesce<number | null>(it?.basePrice, it?.BasePrice, null) ?? null,
                serviceCharge: coalesce<number | null>(it?.serviceCharge, it?.ServiceCharge, null) ?? null,
                tax: coalesce<number | null>(it?.tax, it?.Tax, null) ?? null,
                total: coalesce<number | null>(it?.total, it?.Total, null) ?? null,
                placementNote: coalesce<string | null>(it?.placementNote, it?.PlacementNote, null) ?? null
              }))
            : []
        }))
      : undefined,
    detailedRecommendations: Array.isArray(detailedRecs)
      ? detailedRecs.map((cat: any) => ({
          category: coalesce<string>(cat?.category, cat?.Category) ?? 'Unknown',
          items: Array.isArray(cat?.items ?? cat?.Items)
            ? (cat?.items ?? cat?.Items).map((it: any) => ({
                product: coalesce<string>(it?.product, it?.Product) ?? 'Unnamed',
                recommendedPrice: Number(coalesce<number>(it?.recommendedPrice, it?.RecommendedPrice, 0)),
                changeDelta: coalesce<number | null>(it?.changeDelta, it?.ChangeDelta, null) ?? null,
                changePercent: coalesce<number | null>(it?.changePercent, it?.ChangePercent, null) ?? null,
                why: coalesce<string>(it?.why, it?.Why) ?? '',
                suggestMakingFeesAdditional: coalesce<boolean | null>(it?.suggestMakingFeesAdditional, it?.SuggestMakingFeesAdditional, null) ?? null
              }))
            : []
        }))
      : undefined,
    additionalRecommendationCards: Array.isArray(additionalRecCards)
      ? additionalRecCards.map((c: any) => ({
          title: String(c?.title ?? c?.Title ?? ''),
          items: Array.isArray(c?.items ?? c?.Items) ? (c?.items ?? c?.Items).map((s: any) => String(s)) : []
        }))
      : undefined,
    impact: impactRaw
      ? {
          estimatedAttendeesUsed: Number(
            coalesce<number>(impactRaw?.estimatedAttendeesUsed, impactRaw?.EstimatedAttendeesUsed, 0)
          ),
          itemsPerAttendeeConservative: Number(
            coalesce<number>(impactRaw?.itemsPerAttendeeConservative, impactRaw?.ItemsPerAttendeeConservative, 0)
          ),
          itemsPerAttendeeOptimistic: Number(
            coalesce<number>(impactRaw?.itemsPerAttendeeOptimistic, impactRaw?.ItemsPerAttendeeOptimistic, 0)
          ),
          perCapBase: Number(coalesce<number>(impactRaw?.perCapBase, impactRaw?.PerCapBase, 0)),
          perCapNew: Number(coalesce<number>(impactRaw?.perCapNew, impactRaw?.PerCapNew, 0)),
          perCapDelta: Number(coalesce<number>(impactRaw?.perCapDelta, impactRaw?.PerCapDelta, 0)),
          revenueBase: Number(coalesce<number>(impactRaw?.revenueBase, impactRaw?.RevenueBase, 0)),
          revenueNew: Number(coalesce<number>(impactRaw?.revenueNew, impactRaw?.RevenueNew, 0)),
          revenueDelta: Number(coalesce<number>(impactRaw?.revenueDelta, impactRaw?.RevenueDelta, 0)),
          upliftPercent: Number(coalesce<number>(impactRaw?.upliftPercent, impactRaw?.UpliftPercent, 0)),
          assumptionsNote: coalesce<string | null>(impactRaw?.assumptionsNote, impactRaw?.AssumptionsNote, null) ?? null,
          currency: coalesce<string | null>(impactRaw?.currency, impactRaw?.Currency, null) ?? null
        }
      : undefined,
    serverMetadata: metadata
      ? {
          eventType: coalesce<string>(metadata?.eventType, metadata?.EventType),
          eventLocation: coalesce<string>(metadata?.eventLocation, metadata?.EventLocation),
          eventDate: coalesce<string>(metadata?.eventDate, metadata?.EventDate),
          taxIncluded: coalesce<'Included' | 'Additional' | 'NotApplicable'>(metadata?.taxIncluded, metadata?.TaxIncluded),
          serviceFeeIncluded: coalesce<'Included' | 'Additional' | 'NotApplicable'>(metadata?.serviceFeeIncluded, metadata?.ServiceFeeIncluded),
          eventName: coalesce<string | null>(metadata?.eventName, metadata?.EventName, null) ?? undefined,
          estimatedAttendees: Number(coalesce<number>(metadata?.estimatedAttendees, metadata?.EstimatedAttendees)),
          taxRate: Number(coalesce<number>(metadata?.taxRate, metadata?.TaxRate)),
          serviceFeeRate: Number(coalesce<number>(metadata?.serviceFeeRate, metadata?.ServiceFeeRate))
        }
      : undefined
  };
};

const toSessionStatusResponse = (payload: any): SessionStatusResponse => ({
  sessionId: requireValue(
    coalesce<string>(payload?.sessionId, payload?.SessionId),
    'Session ID was missing from the status response.'
  ),
  email: requireValue(
    coalesce<string>(payload?.email, payload?.Email),
    'Email was missing from the status response.'
  ),
  status: requireValue(
    coalesce<
      'Created' | 'UploadPending' | 'Uploaded' | 'ExtractionComplete' | 'AnalysisComplete' | 'ExtractionInProgress' | 'AnalysisInProgress' | 'FailedExtraction' | 'FailedAnalysis'
    >(payload?.status, payload?.Status),
    'Status was missing from the status response.'
  ),
  createdUtc: requireValue(
    coalesce<string>(payload?.createdUtc, payload?.CreatedUtc),
    'Created timestamp was missing from the status response.'
  ),
  updatedUtc: coalesce<string | undefined>(payload?.updatedUtc, payload?.UpdatedUtc),
  hasFeedback: coalesce<boolean | undefined>(payload?.hasFeedback, payload?.HasFeedback),
  lastError: coalesce<string | null>(payload?.lastError, payload?.LastError, null) ?? null
});

export interface CreateSessionRequest {
  email: string;
  firstName?: string;
  lastName?: string;
}

export interface UploadUrlRequest {
  fileName: string;
  contentType: string;
  fileSizeBytes: number;
}

export const createSession = async (payload: CreateSessionRequest) => {
  const response = await api.post('Labs/MenuAnalysis/Sessions', payload);
  return toCreateSessionResponse(response.data);
};

export const createAnonymousSession = async () => {
  const response = await api.post('Labs/MenuAnalysis/Sessions', {});
  return toCreateSessionResponse(response.data);
};

export interface IdentifySessionRequest {
  email: string;
  firstName?: string;
  lastName?: string;
}

export const identifySession = async (sessionId: string, payload: IdentifySessionRequest) => {
  const response = await api.post(
    `Labs/MenuAnalysis/Sessions/${sessionId}/Identify`,
    payload
  );
  return toSessionStatusResponse(response.data);
};

export const requestUploadUrl = async (sessionId: string, payload: UploadUrlRequest) => {
  const response = await api.post(
    `Labs/MenuAnalysis/Sessions/${sessionId}/UploadUrl`,
    payload
  );
  return toUploadUrlResponse(response.data);
};

export const triggerExtraction = async (sessionId: string): Promise<SessionStatusResponse> => {
  const response = await api.post(
    `Labs/MenuAnalysis/Sessions/${sessionId}/Extract`,
    {}
  );
  return toSessionStatusResponse(response.data);
};

export const submitMetadata = async (sessionId: string, payload: PhaseTwoMetadataRequest): Promise<SessionStatusResponse> => {
  const response = await api.post(
    `Labs/MenuAnalysis/Sessions/${sessionId}/Analyze`,
    payload
  );
  return toSessionStatusResponse(response.data);
};

export const getSessionStatus = async (sessionId: string) => {
  const response = await api.get(
    `Labs/MenuAnalysis/Sessions/${sessionId}`
  );
  return toSessionStatusResponse(response.data);
};

export const getAnalysis = async (sessionId: string) => {
  const response = await api.get(
    `Labs/MenuAnalysis/Sessions/${sessionId}/Analysis`
  );
  return toPhaseTwoAnalysisResponse(response.data);
};

export const getExtraction = async (sessionId: string) => {
  const response = await api.get(
    `Labs/MenuAnalysis/Sessions/${sessionId}/Extraction`
  );
  return toPhaseOneExtractionResponse(response.data);
};

export const submitFeedback = async (sessionId: string, payload: SubmitFeedbackRequest) => {
  await api.post(
    `Labs/MenuAnalysis/Sessions/${sessionId}/Feedback`,
    payload
  );
};
